import React from 'react';
import { Link } from 'react-router-dom';
import type { PaymentOptionDto, SeatPaymentKind } from '../../../models/sessions/Session';
import { money } from '../../../utils/raceNightFormat';
import './PaymentChoice.css';

interface Props {
  /** Whose seat this is: "Your seat", "Guest 1". Names the radio group for screen readers. */
  legend: string;
  /** As the server priced them, in the server's order. Never priced here. */
  options: PaymentOptionDto[];
  value: SeatPaymentKind;
  onChange: (payment: SeatPaymentKind) => void;
  /** Unique per seat on the page: the radio group's name and the stem of every id. */
  idPrefix: string;
  /**
   * Where to buy credits and come back. Given, a credits option greyed because the balance is short
   * links there: greyed, it can't be picked, and picking it was the member's only way to the checkout.
   */
  buyCreditsTo?: string;
}

/** The server's reason when the balance can't cover a seat (RaceNightListService, SessionQuoteService). */
export const NOT_ENOUGH_CREDITS = 'Not enough credits';

const NAMES: Record<SeatPaymentKind, string> = {
  membership: 'Membership',
  included: 'Included',
  credits: 'Credits',
  card: 'Card',
};

const amountOf = (o: PaymentOptionDto): string =>
  (o.payment === 'credits' ? `${o.credits} ${o.credits === 1 ? 'credit' : 'credits'}` : money(o.priceCents));

/**
 * One seat's way to pay, per the PayAtBooking / RaceNightPay boards: a bordered row per option with a
 * radio dot, the name, a grey line (the server's label, or why it can't be used) and the amount.
 *
 * Real radios in a fieldset, so arrow keys, Space and screen readers behave natively. Each radio is
 * NAMED by the option name alone and DESCRIBED by the line and amount: a reason such as "Pay with
 * credits or card" must not make the Included radio answer to /credits/.
 */
const PaymentChoice: React.FC<Props> = ({ legend, options, value, onChange, idPrefix, buyCreditsTo }) => (
  <fieldset className="pay-choice">
    <legend className="pay-choice-legend">{legend}</legend>
    {options.map((o) => {
      const id = `${idPrefix}-${o.payment}`;
      const desc = o.available ? o.label : o.reason;
      const checked = value === o.payment;
      const classes = ['pay-option', checked && o.available ? 'is-selected' : '', o.available ? '' : 'is-unavailable']
        .filter(Boolean).join(' ');
      return (
        <label key={o.payment} className={classes} htmlFor={id}>
          <input
            type="radio"
            className="pay-radio"
            id={id}
            name={idPrefix}
            value={o.payment}
            checked={checked}
            disabled={!o.available}
            onChange={() => onChange(o.payment)}
            aria-labelledby={`${id}-name`}
            aria-describedby={[desc ? `${id}-desc` : '', `${id}-amount`].filter(Boolean).join(' ')}
          />
          <span className="pay-option-text">
            <span id={`${id}-name`} className="pay-option-name">{NAMES[o.payment]}</span>
            {desc && <span id={`${id}-desc`} className="pay-option-desc">{desc}</span>}
            {buyCreditsTo && o.payment === 'credits' && !o.available && o.reason === NOT_ENOUGH_CREDITS && (
              <Link to={buyCreditsTo} className="pay-option-buy">Buy credits</Link>
            )}
          </span>
          <span id={`${id}-amount`} className="pay-option-amount">{amountOf(o)}</span>
        </label>
      );
    })}
  </fieldset>
);

export default PaymentChoice;
